import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Sofa, X } from 'lucide-react';
import type { MessageKey } from '@reos/i18n';
import { useT } from '../lib/i18n.js';
import { mediaSrc } from '../lib/properties.js';
import {
  deletePhotoStaging,
  fetchPhotoStaging,
  publishPhotoStaging,
  startPhotoStaging,
  type PhotoStagingState,
  type PhotoStagingStep,
  type PhotoStagingVariant,
} from '../lib/photo-staging.js';

/**
 * Krok STG1 — „Virtuálně zařídit (AI)" u fotky v galerii zakazky.
 *
 * R64 (R.B. 28. 10. 2026): PLNE AUTOMATICKY. Makler vybere jen typ
 * mistnosti — a ten je uz predvyplneny ze serveru (`suggestedRoomName`:
 * `room_type` fotky, jinak jeji popisek, jinak „Obývací pokoj"). Nic jineho
 * se nezadava.
 *
 * STAV SE DOTAHUJE, ne pushuje: uloha trva 25-40 minut (Blender + GPT), takze
 * otevreny dialog se ptá serveru kazdych 30 s a jinak nic nedela (tyz vzor
 * jako stav videi z fotek).
 *
 * STAV SE CTE AZ PO OTEVRENI dialogu (`load` v `onOpenChange`), ne pri
 * vykresleni galerie: fotek v galerii jsou desitky a stav stagingu je pro
 * kazdou vlastni dotaz — mrizka by jinak pri otevreni zakazky poslala desitky
 * requestu (poučeni o falesne prazdnych obrazovkach pri rate limitu).
 */

const STEP_KEYS = {
  ceka: 'webAdmin.gallery.stagingStep.ceka',
  odeslano: 'webAdmin.gallery.stagingStep.odeslano',
  kalibrace: 'webAdmin.gallery.stagingStep.kalibrace',
  rozvrzeni: 'webAdmin.gallery.stagingStep.rozvrzeni',
  render: 'webAdmin.gallery.stagingStep.render',
  zarovnani: 'webAdmin.gallery.stagingStep.zarovnani',
  hotovo: 'webAdmin.gallery.stagingStep.hotovo',
} as const satisfies Readonly<Record<PhotoStagingStep, MessageKey>>;

/** Jak casto se otevreny dialog ptá na stav (ms). */
const POLL_MS = 30_000;

export interface PhotoStagingPanelProps {
  readonly caseId: string;
  readonly propertyId: string;
  readonly mediaId: string;
  /** Prekresli galerii, kdyz vizualizace pribude nebo zmizi. */
  readonly onChanged: () => void;
}

type View = 'DAY' | 'EVENING' | 'ORIGINAL';

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function PhotoStagingPanel({
  caseId,
  propertyId,
  mediaId,
  onChanged,
}: PhotoStagingPanelProps): React.JSX.Element {
  const t = useT();
  const [open, setOpen] = React.useState(false);
  const [state, setState] = React.useState<PhotoStagingState | null>(null);
  const [roomName, setRoomName] = React.useState<string | null>(null);
  const [view, setView] = React.useState<View>('DAY');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    void fetchPhotoStaging(caseId, mediaId)
      .then((next) => {
        setState(next);
        setRoomName((current) => current ?? next.staging?.roomName ?? next.suggestedRoomName);
      })
      .catch((cause: unknown) => {
        setError(errorText(cause));
      });
  }, [caseId, mediaId]);

  // Dotahovani jen kdyz je dialog otevreny A uloha jeste bezi — hotovy ani
  // spadnuty staging uz svuj stav nezmeni, takze se server neptá zbytecne.
  const running = state?.staging?.status === 'PENDING' || state?.staging?.status === 'RUNNING';
  React.useEffect(() => {
    if (!open || !running) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => { clearInterval(timer); };
  }, [open, running, load]);

  const run = (work: () => Promise<unknown>): void => {
    setBusy(true);
    setError(null);
    void work()
      .then(() => {
        onChanged();
        load();
      })
      .catch((cause: unknown) => {
        setError(errorText(cause));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const staging = state?.staging ?? null;
  const dayMediaId = staging?.dayMediaId ?? null;
  const eveningMediaId = staging?.eveningMediaId ?? null;
  const shownMediaId = view === 'DAY' ? dayMediaId : view === 'EVENING' ? eveningMediaId : mediaId;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) load();
      }}
    >
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="rh-btn"
          aria-label={t('webAdmin.gallery.stagingButton')}
          title={t('webAdmin.gallery.stagingHint')}
          data-testid={`gallery-staging-${mediaId}`}
        >
          <Sofa size={14} aria-hidden="true" />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60" />
        <Dialog.Content
          className="fixed top-10 left-1/2 -translate-x-1/2 rounded-lg shadow-xl p-4 w-full max-w-xl flex flex-col gap-3"
          style={{ background: 'var(--rh-card)', maxHeight: '80vh', overflowY: 'auto' }}
          data-testid="staging-dialog"
        >
          <Dialog.Title className="text-base font-semibold">{t('webAdmin.gallery.stagingButton')}</Dialog.Title>
          <Dialog.Description className="m-0 text-sm" style={{ color: 'var(--rh-text-muted)' }}>
            {t('webAdmin.gallery.stagingHint')}
          </Dialog.Description>

          {error !== null ? (
            <p role="alert" className="text-sm" style={{ color: 'var(--rh-danger)' }} data-testid="staging-error">
              {error}
            </p>
          ) : null}

          {/*
            Vyber mistnosti + spusteni. Ukazuje se, dokud fotka staging nemela,
            A TAKY po neuspechu: kredit je zpatky (worker ho vratil), takze
            makler musi mit kudy zkusit znovu — jinak by fotka zustala navzdy
            „spadnuta". Bezici uloha tlacitko nema (server by vratil 409).
          */}
          {(staging === null || staging.status === 'FAILED') && state !== null ? (
            <div className="flex flex-col gap-2">
              <label className="flex flex-col gap-1 text-sm">
                {t('webAdmin.gallery.stagingRoomLabel')}
                <select
                  className="rh-input"
                  value={roomName ?? state.suggestedRoomName}
                  onChange={(event) => { setRoomName(event.target.value); }}
                  data-testid="staging-room"
                >
                  {/*
                    Predvyplnena mistnost nemusi byt v nabidce sablony (muze
                    prijit z popisku fotky), takze se do vyctu prida zvlast.
                  */}
                  {[
                    ...new Set([state.suggestedRoomName, ...state.roomNameOptions]),
                  ].map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <p className="m-0 text-sm" style={{ color: 'var(--rh-text-muted)' }}>
                {t('webAdmin.gallery.stagingEstimate', {
                  min: state.estimateMinutes.min,
                  max: state.estimateMinutes.max,
                })}{' '}
                {t('webAdmin.gallery.stagingRemaining', { count: state.remainingCasesThisMonth })}
              </p>
              <button
                type="button"
                className="rh-btn rh-btn--primary"
                disabled={busy}
                onClick={() => { run(() => startPhotoStaging(caseId, mediaId, roomName ?? state.suggestedRoomName)); }}
                data-testid="staging-start"
              >
                {t('webAdmin.gallery.stagingStart')}
              </button>
            </div>
          ) : null}

          {/* Bezi: krok a odhad doby. */}
          {running && staging !== null ? (
            <p role="status" className="m-0 text-sm" data-testid="staging-progress">
              {t('webAdmin.gallery.stagingWorking')} {t(STEP_KEYS[staging.step])} ·{' '}
              {t('webAdmin.gallery.stagingEstimate', {
                min: state?.estimateMinutes.min ?? 0,
                max: state?.estimateMinutes.max ?? 0,
              })}
            </p>
          ) : null}

          {staging?.status === 'FAILED' ? (
            <p role="status" className="m-0 text-sm" style={{ color: 'var(--rh-danger)' }} data-testid="staging-failed">
              {t('webAdmin.gallery.stagingFailed')}
            </p>
          ) : null}

          {/* Hotovo: prepinac Zarizeno / Vecer / Skutecny stav + akce. */}
          {staging?.status === 'DONE' ? (
            <div className="flex flex-col gap-2">
              <div className="flex gap-1" role="group" aria-label={t('webAdmin.gallery.stagingButton')}>
                {(
                  [
                    ['DAY', 'webAdmin.gallery.stagingViewStaged'],
                    ['EVENING', 'webAdmin.gallery.stagingViewEvening'],
                    ['ORIGINAL', 'webAdmin.gallery.stagingViewOriginal'],
                  ] as const satisfies readonly (readonly [View, MessageKey])[]
                ).map(([value, key]) => (
                  <button
                    key={value}
                    type="button"
                    className="rh-btn"
                    aria-pressed={view === value}
                    onClick={() => { setView(value); }}
                    data-testid={`staging-view-${value.toLowerCase()}`}
                  >
                    {t(key)}
                  </button>
                ))}
              </div>
              {shownMediaId !== null ? (
                <img
                  /*
                   * `mediaSrc` (ne holá cesta): v dev režimu běží UI a API na
                   * jiném portu, takže relativní `/v1/...` by prohlížeč tahal
                   * z originu web-adminu a obrázek by se nenačetl vůbec.
                   * Tytéž pravidlo, jakým kreslí náhledy galerie.
                   */
                  src={mediaSrc(`/v1/properties/${propertyId}/media/${shownMediaId}?size=full`)}
                  crossOrigin="use-credentials"
                  alt={t('webAdmin.gallery.photoAlt')}
                  className="w-full"
                  data-testid="staging-image"
                />
              ) : null}
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  className="rh-btn"
                  disabled={busy}
                  onClick={() => { run(() => publishPhotoStaging(caseId, mediaId, 'DAY' as PhotoStagingVariant)); }}
                  data-testid="staging-publish-day"
                >
                  {t('webAdmin.gallery.stagingAddDay')}
                </button>
                <button
                  type="button"
                  className="rh-btn"
                  disabled={busy}
                  onClick={() => { run(() => publishPhotoStaging(caseId, mediaId, 'EVENING' as PhotoStagingVariant)); }}
                  data-testid="staging-publish-evening"
                >
                  {t('webAdmin.gallery.stagingAddEvening')}
                </button>
                <button
                  type="button"
                  className="rh-btn"
                  disabled={busy}
                  onClick={() => { run(() => deletePhotoStaging(caseId, mediaId)); }}
                  data-testid="staging-delete"
                >
                  {t('webAdmin.gallery.stagingDelete')}
                </button>
              </div>
            </div>
          ) : null}

          <Dialog.Close asChild>
            <button type="button" className="rh-btn" data-testid="staging-close">
              <X size={14} aria-hidden="true" /> {t('webAdmin.gallery.stagingCancel')}
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
